/*
  assets/gcitypartners-service-continuity.js — GCITYPARTNERS-SERVICE-CONTINUITY-RUNTIME-07
  Updated: 2026-09-10

  Owner: URL-only continuity from GCityPartners service collections to professional products.
  Canonical lane definitions remain owned by assets/gcitypartners-service-lane-map.js.

  Note:
  - Public route context, declared link targets and continuity state attributes only.
  - Visible labels and locale copy remain Liquid-owned.
  - No preventDefault, storage, user-field hydration, checkout, API, GFun or Product Handle mutation.
*/
(function (window, document) {
  'use strict';

  var VERSION = 'gcitypartners-service-continuity-runtime-07';
  var COLLECTION_ROOT = '[data-gcp-collection-continuity]';
  var PRODUCT_ROOT = '[data-gcp-service-continuity]';
  var RELATED_ROOT = '[data-gcp-related-routes]';
  var ACTION_ROOT = '[data-gcp-action-routes]';

  var ALLOWED_LANES = Object.freeze({
    'urban-renewal': true,
    'land-natural-resources': true,
    'property-assets-investment': true,
    'infrastructure-engineering': true,
    'digital-city': true,
    'water-resilience': true,
    'green-city-service': true,
    'industry-park-development': true,
  });

  var COLLECTION_ALIASES = Object.freeze({
    'urban-renewal': 'urban-renewal',
    'real-estate': 'property-assets-investment',
    infrastructure: 'infrastructure-engineering',
    'digital-city': 'digital-city',
    'environmental-sustainability': 'green-city-service',
    'water-marine': 'water-resilience',
    'industry-park': 'industry-park-development',
    'surveying-geomatics': 'infrastructure-engineering',
    'property-asset-valuation': 'property-assets-investment',
    'digital-city-geospatial-analytics': 'digital-city',
    'ecology-environmental-assessment': 'green-city-service',
    'rural-development-agriculture': 'land-natural-resources',
    'industrial-park-planning-development': 'industry-park-development',
    'planning-development': 'land-natural-resources',
    'drone-surveying-operations': 'infrastructure-engineering',
    'hydrographic-marine-surveying': 'water-resilience',
    'water-infrastructure-resilience': 'water-resilience',
    'ai-automation-solutions': 'digital-city',
  });

  /** @type {Set<DirectoryRoot>} */
  const mountedRoots = new Set();
  var stateByRoot = new WeakMap();

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function slug(value) {
    return clean(value)
      .toLowerCase()
      .replace(/_/g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  function params() {
    try {
      return new URLSearchParams(window.location.search || '');
    } catch (_) {
      return new URLSearchParams();
    }
  }

  function collectionListState(search) {
    const input = clean(search);
    if (input.length > 4096) return '';

    const source = new URLSearchParams(input);
    const output = new URLSearchParams();
    const allowedSorts = new Set([
      'manual',
      'best-selling',
      'title-ascending',
      'title-descending',
      'price-ascending',
      'price-descending',
      'created-ascending',
      'created-descending',
    ]);

    let count = 0;

    source.forEach(function (value, key) {
      if (count >= 40 || value.length > 512) return;

      if (key === 'sort_by') {
        if (allowedSorts.has(value)) {
          output.set(key, value);
        }
        return;
      }

      if (key === 'page') {
        if (/^[1-9]\d{0,5}$/.test(value)) {
          output.set(key, value);
        }
        return;
      }

      if (/^filter\.[a-zA-Z0-9_.-]{1,120}$/.test(key)) {
        output.append(key, value);
        count += 1;
      }
    });

    const result = output.toString();
    return result.length <= 4096 ? result : '';
  }

  function localePrefix() {
    var match = clean(window.location.pathname).match(/^\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*(?=\/)/i);
    return match ? match[0] : '';
  }

  function localizedPath(value) {
    var path = clean(value);
    var prefix = localePrefix();
    if (!prefix || path.charAt(0) !== '/' || path.indexOf(prefix + '/') === 0) {
      return path;
    }
    if (
      path.indexOf('/pages/') === 0 ||
      path.indexOf('/collections/') === 0 ||
      path === '/collections' ||
      path.indexOf('/products/') === 0
    ) {
      return prefix + path;
    }
    return path;
  }

  function canonicalLane(value) {
    var raw = slug(value);
    if (!raw) return '';

    var owner = window.GCityPartnersServiceLaneMap;
    if (owner && typeof owner.canonical === 'function') {
      try {
        var resolved = slug(owner.canonical(raw));
        if (ALLOWED_LANES[resolved]) return resolved;
      } catch (_) {}
    }

    var aliased = COLLECTION_ALIASES[raw] || raw;
    return ALLOWED_LANES[aliased] ? aliased : '';
  }

  function requestedLane(fallback) {
    var query = params();
    return canonicalLane(
      query.get('service_lane') ||
        query.get('scenario') ||
        query.get('scene') ||
        query.get('lane') ||
        fallback ||
        ''
    );
  }

  function setQuery(urlValue, values, hash) {
    var raw = clean(urlValue);
    if (!raw) return '';

    try {
      var url = new URL(localizedPath(raw), window.location.origin);
      if (url.origin !== window.location.origin) return '';

      Object.keys(values || {}).forEach(function (key) {
        var value = clean(values[key]);
        if (value) url.searchParams.set(key, value);
      });
      if (hash) url.hash = hash;
      return url.pathname + url.search + url.hash;
    } catch (_) {
      return '';
    }
  }

  function remember(state, node, name) {
    var record = state.originals.get(node);
    if (!record) {
      record = Object.create(null);
      state.originals.set(node, record);
    }
    if (!Object.prototype.hasOwnProperty.call(record, name)) {
      record[name] = node.getAttribute(name);
    }
  }

  function setAttribute(state, node, name, value) {
    if (!node || !clean(value)) return;
    remember(state, node, name);
    node.setAttribute(name, value);
  }

  function restore(state) {
    state.originals.forEach(function (record, node) {
      if (!node) return;
      Object.keys(record).forEach(function (name) {
        var value = record[name];
        if (value == null) {
          node.removeAttribute(name);
        } else {
          node.setAttribute(name, value);
        }
      });
    });
    state.originals.clear();
  }

  function nodes(scopes, selector) {
    var output = [];
    var seen = new Set();

    scopes.forEach(function (scope) {
      if (!scope) return;
      if (scope.matches && scope.matches(selector) && !seen.has(scope)) {
        seen.add(scope);
        output.push(scope);
      }
      if (!scope.querySelectorAll) return;
      Array.prototype.forEach.call(scope.querySelectorAll(selector), function (node) {
        if (seen.has(node)) return;
        seen.add(node);
        output.push(node);
      });
    });

    return output;
  }

  function matchingCollectionHost(handle) {
    var expected = clean(handle);
    var hosts = document.querySelectorAll('[data-module="main-collection"]');
    for (var index = 0; index < hosts.length; index += 1) {
      if (clean(hosts[index].getAttribute('data-collection-handle')) === expected) {
        return hosts[index];
      }
    }
    return null;
  }

  function matchingRelatedRoots(handle) {
    var expected = clean(handle);
    return Array.prototype.slice
      .call(document.querySelectorAll(RELATED_ROOT))
      .filter(function (root) {
        return clean(root.getAttribute('data-product-handle')) === expected;
      });
  }

  function matchingActionRoots(handle) {
    var expected = clean(handle);
    return Array.prototype.slice
      .call(document.querySelectorAll(ACTION_ROOT))
      .filter(function (root) {
        return clean(root.getAttribute('data-product-handle')) === expected;
      });
  }

  function cardLane(link, fallback) {
    var card = link && link.closest ? link.closest('[data-service-lane]') : null;
    return canonicalLane(card && card.getAttribute('data-service-lane')) || canonicalLane(fallback);
  }

  function enhanceCollection(root, state) {
    var sourceCollection = clean(root.getAttribute('data-collection-handle'));
    var collectionHost = matchingCollectionHost(sourceCollection);
    var scopes = [root];
    if (collectionHost) scopes.push(collectionHost);

    var pageLane = requestedLane(root.getAttribute('data-market-handle') || sourceCollection);
    if (pageLane) {
      document.documentElement.setAttribute('data-gcp-active-service-lane', pageLane);
      state.activeLane = pageLane;
    }

    nodes(scopes, '[data-gcp-product-route]').forEach(function (link) {
      var lane = pageLane || cardLane(link, '');
      if (!lane) return;

      const next = setQuery(link.getAttribute('href'), {
        source: 'gcitypartners-collection',
        service_lane: lane,
        source_collection: sourceCollection,
      });

      if (next) {
        const nextUrl = new URL(next, window.location.origin);
        const expectedPath = localizedPath('/collections/' + encodeURIComponent(sourceCollection));

        const listState =
          window.location.pathname === expectedPath
            ? collectionListState(window.location.search)
            : '';

        nextUrl.searchParams.delete('source_list_state');

        if (listState) {
          nextUrl.searchParams.set('source_list_state', listState);
        }

        setAttribute(state, link, 'href', nextUrl.pathname + nextUrl.search + nextUrl.hash);
      }
    });

    nodes(scopes, '[data-gcp-product-review-route]').forEach(function (link) {
      var lane = cardLane(link, pageLane);
      if (!lane) return;

      var next = setQuery(
        link.getAttribute('href'),
        {
          source: 'gcitypartners-collection-card',
          service_lane: lane,
          scenario: lane,
          source_collection: sourceCollection,
        },
        '#gcp-brief-form'
      );
      if (next) setAttribute(state, link, 'href', next);
    });
  }

  function enhanceProduct(root, state) {
    var fallback =
      root.getAttribute('data-default-service-lane') || root.getAttribute('data-gcp-service-lane');
    var lane = requestedLane(fallback) || canonicalLane(fallback);
    if (!lane) {
      root.setAttribute('data-gcp-continuity-state', 'invalid-service-lane');
      return;
    }

    var handle = clean(root.getAttribute('data-product-handle'));
    var sourceCollection = clean(params().get('source_collection'));
    var scopes = [root].concat(matchingRelatedRoots(handle)).concat(matchingActionRoots(handle));

    let returnCollections = [];

    try {
      const parsedCollections = JSON.parse(
        root.getAttribute('data-gcp-return-collections') || '[]'
      );

      if (Array.isArray(parsedCollections)) {
        returnCollections = parsedCollections.filter(function (value) {
          return typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(value);
        });
      }
    } catch (_) {
      returnCollections = [];
    }

    const hasVerifiedSourceCollection =
      /^[a-z0-9][a-z0-9-]*$/.test(sourceCollection) && returnCollections.includes(sourceCollection);

    const returnPath = hasVerifiedSourceCollection
      ? '/collections/' + encodeURIComponent(sourceCollection)
      : '/collections';

    const returnUrl = new URL(
      setQuery(returnPath, {
        source: 'service-product',
        service_lane: lane,
      }),
      window.location.origin
    );

    if (hasVerifiedSourceCollection) {
      const savedListState = collectionListState(params().get('source_list_state') || '');

      new URLSearchParams(savedListState).forEach(function (value, key) {
        returnUrl.searchParams.append(key, value);
      });
    }

    const solutionsUrl = returnUrl.pathname + returnUrl.search + returnUrl.hash;
    var gatewayUrl = setQuery(
      '/pages/gcitypartners',
      {
        source: 'service-product',
        intent: 'service-route',
        scenario: lane,
        service_lane: lane,
      },
      '#gcp-scene-gateway'
    );
    var reviewUrl = setQuery(
      '/pages/gcitypartners',
      {
        source: 'service-product',
        intent: 'review-brief',
        scenario: lane,
        service_lane: lane,
        product: handle,
        source_collection: sourceCollection,
      },
      '#gcp-brief-form'
    );
    var contactUrl = setQuery(
      '/pages/gcitypartners',
      {
        source: 'service-product',
        intent: 'professional-review',
        scenario: lane,
        service_lane: lane,
        product: handle,
        source_collection: sourceCollection,
      },
      '#gcp-brief-form'
    );

    setAttribute(state, root, 'data-gcp-service-lane', lane);
    root.setAttribute('data-gcp-continuity-state', 'ready');
    document.documentElement.setAttribute('data-gcp-active-service-lane', lane);
    state.activeLane = lane;

    nodes(scopes, RELATED_ROOT).forEach(function (node) {
      setAttribute(state, node, 'data-active-service-lane', lane);
    });
    nodes(scopes, '[data-gcp-lane-route]').forEach(function (node) {
      setAttribute(state, node, 'href', gatewayUrl);
    });
    nodes(scopes, '[data-gcp-solutions-route]').forEach(function (node) {
      setAttribute(state, node, 'href', solutionsUrl);
    });

    const returnLabel = root.getAttribute(
      hasVerifiedSourceCollection ? 'data-gcp-return-original-label' : 'data-gcp-return-all-label'
    );

    if (returnLabel) {
      root
        .querySelectorAll(
          '.psw-continuity-nav [data-gcp-solutions-route], ' +
            '.psw-action-row [data-gcp-solutions-route]'
        )
        .forEach(function (node) {
          node.textContent = node.closest('.psw-continuity-nav') ? '← ' + returnLabel : returnLabel;
        });
    }
    nodes(scopes, '[data-gcp-review-route], [data-gcp-service-review-route]').forEach(
      function (node) {
        setAttribute(state, node, 'href', reviewUrl);
      }
    );
    nodes(scopes, '[data-gcp-contact-route]').forEach(function (node) {
      setAttribute(state, node, 'href', contactUrl);
    });
  }

  function refresh(root) {
    var state = stateByRoot.get(root);
    if (!state || !root.isConnected) return;
    restore(state);
    root.removeAttribute('data-gcp-continuity-state');

    if (root.matches(COLLECTION_ROOT)) enhanceCollection(root, state);
    else if (root.matches(PRODUCT_ROOT)) enhanceProduct(root, state);
  }

  function mount(root) {
    if (!root || stateByRoot.has(root)) return;
    var state = {
      originals: new Map(),
      activeLane: '',
    };
    stateByRoot.set(root, state);
    mountedRoots.add(root);
    root.setAttribute('data-gcp-continuity-runtime', VERSION);
    refresh(root);
  }

  function destroy(root) {
    var state = root ? stateByRoot.get(root) : null;
    if (!state) return;

    restore(state);
    if (
      state.activeLane &&
      document.documentElement.getAttribute('data-gcp-active-service-lane') === state.activeLane
    ) {
      document.documentElement.removeAttribute('data-gcp-active-service-lane');
    }

    mountedRoots.delete(root);
    stateByRoot.delete(root);
    root.removeAttribute('data-gcp-continuity-runtime');
    root.removeAttribute('data-gcp-continuity-state');
  }

  function mountAll(scope) {
    var target = scope || document;
    [COLLECTION_ROOT, PRODUCT_ROOT].forEach(function (selector) {
      if (target.matches && target.matches(selector)) mount(target);
      if (!target.querySelectorAll) return;
      Array.prototype.forEach.call(target.querySelectorAll(selector), mount);
    });
  }

  function destroyAll(scope) {
    var target = scope || document;
    [COLLECTION_ROOT, PRODUCT_ROOT].forEach(function (selector) {
      if (target.matches && target.matches(selector)) destroy(target);
      if (!target.querySelectorAll) return;
      Array.prototype.forEach.call(target.querySelectorAll(selector), destroy);
    });
  }

  // URL-only enhancement: a Collection deep link opens its existing native
  // service disclosure without creating a parallel selector or a checkout path.
  function revealTargetedServiceScope() {
    if (
      !window.location ||
      typeof window.location.hash !== 'string' ||
      typeof document.getElementById !== 'function'
    )
      return;
    var hash = window.location.hash.slice(1);
    var id;
    try {
      id = decodeURIComponent(hash);
    } catch (_) {
      return;
    }
    if (!/^[a-z0-9-]{3,100}$/.test(id)) return;
    var target = document.getElementById(id);
    var card = target && target.closest && target.closest('[data-glg-collection-scope-service]');
    if (!card) return;
    var disclosure = card.querySelector('[data-glg-scope-details]');
    if (disclosure) disclosure.open = true;
  }

  function refreshAll() {
    mountedRoots.forEach(function (root) {
      refresh(root);
    });
    document.documentElement.setAttribute('data-gcp-continuity-runtime', VERSION);
    revealTargetedServiceScope();
  }

  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target || document);
    refreshAll();
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target || document);
  });
  window.addEventListener('pageshow', refreshAll);
  window.addEventListener('popstate', refreshAll);
  window.addEventListener('hashchange', revealTargetedServiceScope);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
        refreshAll();
      },
      { once: true }
    );
  } else {
    mountAll(document);
    refreshAll();
  }

  window.GCityPartnersServiceContinuity = Object.freeze({
    version: VERSION,
    init: refreshAll,
    canonicalLane: canonicalLane,
  });
})(window, document);
